import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PLAN_FEATURES, PLANS } from "@/data/landing";
import { annualSavings, annualTotal } from "@/lib/pricing";
import { useLocalizedPath } from "@/i18n/useLocalizedPath";
import { fadeUp, stagger, viewportOnce } from "./motion-presets";

export const PricingSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("landing");
  const lp = useLocalizedPath();
  // Ventas: la vista por defecto enseña UN precio, el mensual. El anual sigue
  // disponible en el toggle, pero deja de ser lo primero que se lee.
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="relative py-24 px-4">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
        className="container mx-auto max-w-6xl"
      >
        <div className="text-center max-w-2xl mx-auto mb-12">
          <motion.span
            variants={fadeUp}
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {t("pricing.eyebrow")}
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-3 font-display text-3xl md:text-5xl font-semibold tracking-tight text-foreground"
          >
            {t("pricing.title")}
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-muted-foreground">
            {t("pricing.subtitle")}
          </motion.p>

          <motion.div variants={fadeUp} className="mt-7 inline-flex">
            <BillingToggle annual={annual} setAnnual={setAnnual} />
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const price = annual ? plan.price_annual : plan.price_monthly;
            const Icon = plan.icon;
            return (
              <motion.div
                key={plan.id}
                variants={fadeUp}
                className={`relative rounded-xl border p-6 flex flex-col bg-card/40 backdrop-blur transition-colors hover:bg-card/60 ${
                  plan.popular ? "border-primary/40 ring-1 ring-primary/20" : "border-border"
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground border-0">
                    {t("pricing.mostPopular")}
                  </Badge>
                )}

                <div className="flex items-center gap-3">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-display text-base font-semibold text-foreground">{t(`pricing.plans.${plan.id}.name`)}</div>
                    <div className="text-xs text-muted-foreground">{t(`pricing.plans.${plan.id}.description`)}</div>
                  </div>
                </div>

                <div className="mt-6 flex items-baseline gap-1">
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={price}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                      className="font-display text-5xl font-semibold tracking-tight text-foreground tabular-nums"
                    >
                      ${price}
                    </motion.span>
                  </AnimatePresence>
                  <span className="text-sm text-muted-foreground">
                    {t("pricing.perMonth")}
                    {annual ? ` ${t("pricing.billedAnnually")}` : ""}
                  </span>
                </div>
                {/* El cargo real, en UNA línea junto al ahorro. `price` es el
                    equivalente mensual de pagar por año; Stripe cobra 12× de una
                    vez. Sin esta cifra el cliente ponía la tarjeta leyendo 229 y
                    recibía 2.748 (projects#369). En mensual no va nada: el
                    contrato de copy es un solo número y ninguna comparación. */}
                <div className="mt-1 h-5 text-xs">
                  {annual && (
                    <div className="font-medium text-foreground tabular-nums">
                      {t("pricing.annualChargeAndSavings", {
                        total: annualTotal(plan.price_annual).toLocaleString("en-US"),
                        savings: annualSavings(plan.price_monthly, plan.price_annual).toLocaleString("en-US"),
                      })}
                    </div>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("pricing.techsIncluded", { count: plan.workers })}
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {PLAN_FEATURES.map((f) => {
                    const included =
                      plan.id === "starter" ? f.starter : plan.id === "professional" ? f.professional : f.business;
                    const comingSoon = included && f.comingSoon;
                    return (
                      <li
                        key={f.key}
                        className={`flex items-center gap-2 text-sm ${included ? "text-foreground/85" : "text-muted-foreground/50"}`}
                      >
                        {comingSoon ? (
                          <Clock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        ) : included ? (
                          <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        ) : (
                          <X className="h-4 w-4 flex-shrink-0" />
                        )}
                        <span>{t(`pricing.features.${f.key}`)}</span>
                        {comingSoon && (
                          <span className="text-[10px] uppercase tracking-wide text-amber-500 border border-amber-500/30 rounded px-1 py-0.5 whitespace-nowrap">
                            {t("pricing.comingSoon")}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>

                <Button
                  size="lg"
                  className={`mt-7 w-full gap-2 ${plan.popular ? "btn-gradient text-white border-0" : ""}`}
                  variant={plan.popular ? "default" : "outline"}
                  // `plan` preselecciona la tarjeta en el selector de /auth y
                  // `billing` la cadencia — sin el segundo, quien cambia el
                  // toggle a anual lee "$229/mes" aquí y ve $289 en el alta.
                  // Ninguno de los dos salta el paso: el usuario sigue
                  // confirmando en el selector (projects#329).
                  onClick={() => navigate(`${lp("/auth")}?mode=signup&plan=${plan.id}&billing=${annual ? "annual" : "monthly"}`)}
                >
                  {t(`pricing.plans.${plan.id}.cta`)} <ArrowRight className="h-4 w-4" />
                </Button>
              </motion.div>
            );
          })}
        </div>

        <motion.p variants={fadeUp} className="text-center text-xs text-muted-foreground mt-8">
          {t("pricing.extraTechs")}
        </motion.p>

        <TeamCostEstimator annual={annual} />
      </motion.div>
    </section>
  );
};

// Interactive price transparency: pick a crew size, see the real monthly
// total from our own price list. No competitor claims, no surprises.
const TeamCostEstimator = ({ annual }: { annual: boolean }) => {
  const { t } = useTranslation("landing");
  const [techs, setTechs] = useState(4);

  const options = PLANS.map((plan) => {
    const base = annual ? plan.price_annual : plan.price_monthly;
    const extra = Math.max(0, techs - plan.workers);
    return { plan, extra, total: base + extra * plan.extraTechPrice };
  });
  const best = options.reduce((a, b) => (b.total <= a.total ? b : a));

  return (
    <motion.div
      variants={fadeUp}
      className="mx-auto mt-12 max-w-xl rounded-xl border border-border bg-card/40 p-6 backdrop-blur"
    >
      <div className="text-center">
        <h3 className="font-display text-lg font-semibold text-foreground">{t("pricing.estimator.title")}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{t("pricing.estimator.subtitle")}</p>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <input
          type="range"
          min={1}
          max={30}
          value={techs}
          onChange={(e) => setTechs(Number(e.target.value))}
          aria-label={t("pricing.estimator.sliderLabel")}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-border accent-primary"
        />
        <span className="w-28 shrink-0 text-right text-sm font-medium text-foreground tabular-nums">
          {t("pricing.estimator.techCount", { count: techs })}
        </span>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
        <div>
          <div className="text-xs text-muted-foreground">{t("pricing.estimator.bestValue")}</div>
          <div className="text-sm font-semibold text-foreground">{t(`pricing.plans.${best.plan.id}.name`)}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-semibold text-foreground tabular-nums">
            ${best.total}
            <span className="text-sm font-normal text-muted-foreground">
              {t("pricing.perMonth")}
              {annual ? ` ${t("pricing.billedAnnually")}` : ""}
            </span>
          </div>
          {annual && (
            <div className="text-[11px] font-medium text-foreground tabular-nums">
              {t("pricing.annualTotal", { total: annualTotal(best.total).toLocaleString("en-US") })}
            </div>
          )}
          <div className="text-[11px] text-muted-foreground tabular-nums">
            {best.extra > 0
              ? t("pricing.estimator.breakdownExtra", {
                  included: best.plan.workers,
                  extra: best.extra,
                  price: best.plan.extraTechPrice,
                })
              : t("pricing.estimator.breakdownIncluded", { included: best.plan.workers })}
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-[11px] text-muted-foreground">{t("pricing.estimator.note")}</p>
    </motion.div>
  );
};

const BillingToggle = ({
  annual,
  setAnnual,
}: {
  annual: boolean;
  setAnnual: (v: boolean) => void;
}) => {
  const { t } = useTranslation("landing");
  return (
    <div className="relative inline-flex items-center rounded-full border border-border bg-card/40 p-1">
      <motion.span
        aria-hidden
        className="absolute top-1 bottom-1 rounded-full bg-primary"
        initial={false}
        animate={{
          left: annual ? "50%" : "0.25rem",
          right: annual ? "0.25rem" : "50%",
        }}
        transition={{ type: "spring", stiffness: 320, damping: 30 }}
      />
      <button
        onClick={() => setAnnual(false)}
        className={`relative z-10 text-sm font-medium px-5 py-1.5 rounded-full transition-colors ${
          !annual ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {t("pricing.monthly")}
      </button>
      <button
        onClick={() => setAnnual(true)}
        className={`relative z-10 inline-flex items-center gap-2 text-sm font-medium px-5 py-1.5 rounded-full transition-colors ${
          annual ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {t("pricing.annual")}
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full transition-colors ${
            annual ? "bg-white/25 text-white" : "bg-success/15 text-success"
          }`}
        >
          {t("pricing.annualDiscount")}
        </span>
      </button>
    </div>
  );
};
